import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BulkKontrollterminGenerationService, BulkGenerationRequest, BulkGenerationProgress } from '../services/bulk-kontrolltermin-generation.service';
import { DashboardStatsService, DashboardStats } from '../services/dashboard-stats.service';
import { KontrollterminService, Kontrolltermin } from '../services/kontrolltermin.service';
import { ReportService, Report } from '../services/report.service';

export interface BulkGenerationConfig {
  year: number;
  fromMonth: number;
  toMonth: number;
  testRun: boolean;
}

export interface MonthOption {
  value: number;
  label: string;
}

export interface BulkGenerationResults {
  totalCreated: number;
  totalSkipped: number;
  totalErrors: number;
  processedStandorte: number;
  standortResults: Array<{
    standortId: string;
    standortName: string;
    created: number;
    skipped: number;
    errors: number;
    errorMessage?: string;
  }>;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  // Year and month configuration
  currentYear = new Date().getFullYear();
  nextYear = this.currentYear + 1;
  
  bulkGenerationConfig: BulkGenerationConfig = {
    year: this.currentYear,
    fromMonth: this.getCurrentMonth(),
    toMonth: 12,
    testRun: false
  };
  
  isBulkGenerating = false;
  bulkGenerationProgress: BulkGenerationProgress | null = null;
  bulkGenerationResults: BulkGenerationResults | null = null;
  showBulkGenerationInterface = false;
  
  // Available months (dynamic based on year)
  availableFromMonths: MonthOption[] = [];
  availableToMonths: MonthOption[] = [];
  
  months = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' }
  ];

  // Dashboard data
  stats: DashboardStats = {
    kontrolltermine_aktuell: 0,
    kontrolltermine_bearbeitung: 0,
    kontrolltermine_freigabe: 0,
    anzahl_kunden: 0,
    anzahl_standorte: 0
  };

  // Real data for next freigaben
  naechsteFreigaben: any[] = [];

  // Real data for latest reports
  letzteBerichte: any[] = [];

  // Sample data for recent activities
  letzteAktivitaeten = [
    { aktion: 'Neuer Kontrolltermin erstellt', kunde: 'Austrian Bank', zeit: 'vor 30 Min', farbe: 'blue' },
    { aktion: 'Bericht hochgeladen', kunde: 'TechCorp', zeit: 'vor 2 Stunden', farbe: 'green' },
    { aktion: 'Projektaufgabe aktualisiert', kunde: 'Innovation GmbH', zeit: 'vor 4 Stunden', farbe: 'purple' },
    { aktion: 'Freigabe genehmigt', kunde: 'Startup Solutions', zeit: 'vor 6 Stunden', farbe: 'orange' }
  ];

  constructor(
    private router: Router,
    private bulkGenerationService: BulkKontrollterminGenerationService,
    private dashboardStatsService: DashboardStatsService,
    private kontrollterminService: KontrollterminService,
    private reportService: ReportService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.initializeAvailableMonths();
    this.loadDashboardStats();
    this.loadFreigabeData();
    this.loadReportsData();
    console.log('Dashboard initialized');
  }

  private loadDashboardStats(): void {
    // Try the protected route first
    this.dashboardStatsService.getStats().subscribe({
      next: (response) => {
        if (response.success) {
          this.stats = response.data;
          console.log('✅ Dashboard stats loaded from protected route:', response.data);
        }
      },
      error: (error) => {
        console.error('❌ Error loading dashboard stats from protected route:', error);
        
        // Fallback to public test route
        console.log('🔄 Trying public test route...');
        this.dashboardStatsService.getTestStats().subscribe({
          next: (response) => {
            if (response.success) {
              this.stats = response.data;
              console.log('✅ Dashboard stats loaded from public test route:', response.data);
            }
          },
          error: (testError) => {
            console.error('❌ Error loading dashboard stats from public test route:', testError);
            this.snackBar.open('Fehler beim Laden der Dashboard-Statistiken', 'Schließen');
          }
        });
      }
    });
  }

  private loadFreigabeData(): void {
    this.kontrollterminService.getKontrolltermineByStatus('freigabe').subscribe({
      next: (kontrolltermine: Kontrolltermin[]) => {
        this.naechsteFreigaben = kontrolltermine.map(kt => ({
          id: kt.id,
          kunde: kt.kunde?.suchbegriff || 'Unbekannter Kunde',
          standort: kt.standort?.name || 'Unbekannter Standort',
          faellig: this.formatDate(kt.datum_durchfuerung || ''),
          tage: this.calculateDaysUntil(kt.datum_durchfuerung || ''),
          status: this.getFreigabeStatusColor(kt.datum_durchfuerung || ''),
          einsatznummer: kt.einsatznummer
        }));
        console.log('✅ Freigabe data loaded:', this.naechsteFreigaben);
      },
      error: (error) => {
        console.error('❌ Error loading freigabe data:', error);
        // Keep empty array on error
        this.naechsteFreigaben = [];
      }
    });
  }

  private formatDate(dateString: string): string {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private calculateDaysUntil(dateString: string): number {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  private getFreigabeStatusColor(dateString: string): string {
    const days = this.calculateDaysUntil(dateString);
    if (days < 0) return 'red';      // Overdue
    if (days <= 3) return 'red';     // Urgent (0-3 days)
    if (days <= 7) return 'yellow';   // Soon (4-7 days)
    return 'green';                  // Future (8+ days)
  }

  private loadReportsData(): void {
    this.reportService.getReports({ limit: 50 }).subscribe({
      next: (response) => {
        if (response.reports) {
          // Get the latest version of each standort
          const latestReportsByStandort = this.getLatestReportsByStandort(response.reports);
          
          // Take only the first 10
          this.letzteBerichte = latestReportsByStandort.slice(0, 10).map(report => ({
            id: report.id,
            titel: this.generateReportTitle(report),
            kunde: report.client_name || 'Unbekannter Kunde',
            standort: report.standort_name || 'Unbekannter Standort',
            datum: this.formatDate(report.created_at),
            prioritaet: this.getPriorityFromStatus(report.status),
            farbe: this.getReportStatusColor(report.status),
            version: report.version,
            status: report.status
          }));
          
          console.log('✅ Reports data loaded:', this.letzteBerichte);
        }
      },
      error: (error) => {
        console.error('❌ Error loading reports data:', error);
        // Keep empty array on error
        this.letzteBerichte = [];
      }
    });
  }

  private getLatestReportsByStandort(reports: Report[]): Report[] {
    // Group by standort_id and get the latest version of each
    const groupedByStandort = reports.reduce((acc, report) => {
      const standortId = report.standort_id || 'unknown';
      if (!acc[standortId] || report.version > acc[standortId].version) {
        acc[standortId] = report;
      }
      return acc;
    }, {} as Record<string, Report>);

    // Convert back to array and sort by created_at (newest first)
    return Object.values(groupedByStandort)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  private generateReportTitle(report: Report): string {
    const date = new Date(report.created_at);
    const monthYear = date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    return `Kontrollbericht ${monthYear}`;
  }

  private getPriorityFromStatus(status: string): string {
    switch (status) {
      case 'sent': return 'Versendet';
      case 'generated': return 'Generiert';
      default: return 'Unbekannt';
    }
  }

  private getReportStatusColor(status: string): string {
    switch (status) {
      case 'sent': return 'green';
      case 'generated': return 'blue';
      default: return 'gray';
    }
  }
  
  private getCurrentMonth(): number {
    return new Date().getMonth() + 1;
  }
  
  private initializeAvailableMonths(): void {
    this.updateAvailableFromMonths();
    this.updateAvailableToMonths();
  }
  
  private updateAvailableFromMonths(): void {
    if (this.bulkGenerationConfig.year === this.currentYear) {
      // Current year: only future months
      const currentMonth = this.getCurrentMonth();
      this.availableFromMonths = this.months.filter(month => month.value >= currentMonth);
    } else {
      // Next year: all months
      this.availableFromMonths = [...this.months];
    }
  }
  
  private updateAvailableToMonths(): void {
    this.availableToMonths = this.months.filter(month => 
      month.value >= this.bulkGenerationConfig.fromMonth
    );
    
    // If toMonth is now invalid, reset it
    if (this.bulkGenerationConfig.toMonth < this.bulkGenerationConfig.fromMonth) {
      this.bulkGenerationConfig.toMonth = this.bulkGenerationConfig.fromMonth;
    }
  }
  
  onYearChange(): void {
    // Reset months when year changes
    if (this.bulkGenerationConfig.year === this.currentYear) {
      // Current year: start from current month
      this.bulkGenerationConfig.fromMonth = this.getCurrentMonth();
    } else {
      // Next year: start from January
      this.bulkGenerationConfig.fromMonth = 1;
    }
    
    this.bulkGenerationConfig.toMonth = 12;
    this.initializeAvailableMonths();
  }
  
  onMonthRangeChange(): void {
    this.updateAvailableToMonths();
  }
  
  isValidMonthRange(): boolean {
    if (this.bulkGenerationConfig.year === this.currentYear) {
      // Current year: must be future months
      const currentMonth = this.getCurrentMonth();
      return this.bulkGenerationConfig.fromMonth >= currentMonth && 
             this.bulkGenerationConfig.toMonth >= this.bulkGenerationConfig.fromMonth;
    } else {
      // Next year: any valid range
      return this.bulkGenerationConfig.toMonth >= this.bulkGenerationConfig.fromMonth;
    }
  }
  
  getMonthLabel(monthValue: number): string {
    const month = this.months.find(m => m.value === monthValue);
    return month ? month.label : '';
  }
  
  toggleBulkGenerationInterface(): void {
    this.showBulkGenerationInterface = !this.showBulkGenerationInterface;
  }
  
  startBulkGeneration(isTestRun: boolean): void {
    if (!this.isValidMonthRange()) {
      const yearLabel = this.bulkGenerationConfig.year === this.currentYear ? 
        'aktuelles Jahr' : 'nächstes Jahr';
      this.snackBar.open(`Bitte wählen Sie einen gültigen Monatsbereich für das ${yearLabel}`, 'Schließen');
      return;
    }
    
    this.bulkGenerationConfig.testRun = isTestRun;
    this.isBulkGenerating = true;
    this.bulkGenerationResults = null;
    
    this.bulkGenerationService.generateForAllStandorte({
      year: this.bulkGenerationConfig.year,
      from_month: this.bulkGenerationConfig.fromMonth,
      to_month: this.bulkGenerationConfig.toMonth,
      include_inactive: false, // Always false
      test_run: isTestRun
    }).subscribe({
      next: (response) => {
        this.pollBulkGeneration(response.bulk_run_id);
      },
      error: (error) => {
        this.isBulkGenerating = false;
        this.snackBar.open('Fehler bei der Bulk-Generierung', 'Schließen');
        console.error('Error starting bulk generation:', error);
      }
    });
  }
  
  private pollBulkGeneration(bulkRunId: string): void {
    const pollInterval = setInterval(() => {
      this.bulkGenerationService.getBulkRun(bulkRunId).subscribe({
        next: (response) => {
          this.bulkGenerationProgress = response.data;
          
          if (response.data.run.finished_at) {
            clearInterval(pollInterval);
            this.isBulkGenerating = false;
            this.bulkGenerationResults = this.processBulkResults(response.data);
            
            const message = this.bulkGenerationConfig.testRun ? 
              `Test Run: ${response.data.run.created_count} Kontrolltermine würden erstellt werden` :
              `Bulk-Generierung abgeschlossen: ${response.data.run.created_count} Kontrolltermine erstellt`;
            
            this.snackBar.open(message, 'Schließen', { duration: 5000 });
          }
        },
        error: (error) => {
          clearInterval(pollInterval);
          this.isBulkGenerating = false;
          this.snackBar.open('Fehler beim Abrufen des Bulk-Generierungsstatus', 'Schließen');
        }
      });
    }, 3000); // Poll every 3 seconds
  }
  
  private processBulkResults(data: BulkGenerationProgress): BulkGenerationResults {
    return {
      totalCreated: data.run.created_count,
      totalSkipped: data.run.skipped_existing_count,
      totalErrors: data.run.errors_count,
      processedStandorte: data.run.total_candidates,
      standortResults: data.items.map(item => ({
        standortId: item.projektaufgabe_id || 'unknown',
        standortName: item.message,
        created: item.action === 'created' ? 1 : 0,
        skipped: item.action === 'skipped' ? 1 : 0,
        errors: item.action === 'error' ? 1 : 0,
        errorMessage: item.action === 'error' ? item.message : undefined
      }))
    };
  }

  // Method to get status color class for freigabe items
  getStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      gray: 'bg-gray-500',
      red: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  }

  // Method to get priority color classes for reports
  getPriorityColor(prioritaet: string): { bg: string; text: string } {
    const colors: { [key: string]: { bg: string; text: string } } = {
      'Hoch': { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-200' },
      'Mittel': { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200' },
      'Niedrig': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200' },
      'Info': { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' }
    };
    return colors[prioritaet] || { bg: 'bg-gray-100 dark:bg-gray-900', text: 'text-gray-800 dark:text-gray-200' };
  }

  // Navigation methods
  navigateToFreigabe(freigabe: any): void {
    console.log('Navigating to freigabe:', freigabe);
    // Navigate to kontrolltermine with freigabe status filter
    this.router.navigate(['/kontrolltermine'], { queryParams: { status: 'freigabe' } });
  }

  navigateToBericht(bericht: any): void {
    console.log('Navigating to bericht:', bericht);
    // Navigate to reports list or specific report detail
    this.router.navigate(['/reports'], { queryParams: { titel: bericht.titel } });
  }

  // Navigation method for stats cards
  navigateToStats(type: string): void {
    console.log('Navigating to stats:', type);
    
    switch (type) {
      case 'freigaben':
        this.router.navigate(['/freigaben']);
        break;
      case 'clients':
        this.router.navigate(['/clients']);
        break;
      case 'techniker':
        this.router.navigate(['/techniker']);
        break;
      case 'kontrolltermine':
        this.router.navigate(['/kontrolltermine']);
        break;
      case 'standorte':
        this.router.navigate(['/standorte']);
        break;
      case 'bearbeitung':
        // Navigate to kontrolltermine with status filter for "in bearbeitung"
        this.router.navigate(['/kontrolltermine'], { queryParams: { status: 'bearbeitung' } });
        break;
      case 'freigabe':
        // Navigate to kontrolltermine with status filter for "freigabe"
        this.router.navigate(['/kontrolltermine'], { queryParams: { status: 'freigabe' } });
        break;
      default:
        console.log('Unknown stats type:', type);
    }
  }
}
